// update-downgrade-chat-delegated-guard.ts — security F-01 (06k review).
//
// `avatar update` mặc định theo `latest`: trên beta 3.38 mà latest là 3.37.x thì
// update HẠ cấp. 3.37 không biết `chat_delegated` (không validate inbox, MR ghi
// quyết định Claude là "người"). Luật: hạ semver ⇒ từ chối trừ `--allow-downgrade`;
// có cờ mà workspace đăng ký còn directive ủy nhiệm chưa xử lý hoặc mission chưa
// archive có `gate_approved.source=chat_delegated` hoặc `decision_answered.actor =
// chat_delegated:<os>` (N-6) ⇒ vẫn từ chối. Lỗi đọc từng
// workspace/mission bỏ qua (fail-soft như registry) — không chặn update vì rác.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { compareParsedSemVer, parseSemVerLoose } from "../compare-semver-with-prerelease.js";
import { DEFAULT_REGISTRY_PATH, readWorkspaceRegistry } from "../workspace-registry-store.js";
import { CHAT_DELEGATED_SOURCE, isChatDelegatedActor } from "./chat-delegated-attribution.js";
import {
  isDirectiveProcessed,
  listInboxDirectiveIds,
  readInboxDirectiveRaw,
} from "./directive-inbox.js";
import { scanJournal } from "./journal-scan-report.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";

/** `target` thấp hơn `current` theo semver (prerelease < release). Không parse được ⇒ false. */
export function isDowngrade(current: string, target: string): boolean {
  const from = parseSemVerLoose(current);
  const to = parseSemVerLoose(target);
  if (from === null || to === null) return false;
  return compareParsedSemVer(to, from) < 0;
}

async function missionBlocker(controlRoot: string): Promise<string | undefined> {
  for (const id of await listInboxDirectiveIds(controlRoot)) {
    if (await isDirectiveProcessed(controlRoot, id)) continue;
    const raw = (await readInboxDirectiveRaw(controlRoot, id)) as { source?: unknown } | null;
    if (raw?.source === CHAT_DELEGATED_SOURCE) return `directive ủy nhiệm ${id} chưa xử lý`;
  }
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  const delegated = scan.records.some(
    ({ delta }) =>
      (delta.kind === "gate_approved" && delta.source === CHAT_DELEGATED_SOURCE) ||
      // N-6: câu trả lời Claude thay chủ mission (actor `chat_delegated:<os>`).
      (delta.kind === "decision_answered" &&
        typeof delta.actor === "string" &&
        isChatDelegatedActor(delta.actor)),
  );
  return delegated ? "có gate duyệt/quyết định ủy nhiệm (chat_delegated), chưa archive" : undefined;
}

/** Mỗi phần tử: `<workspace>: mission <id> — <lý do>`. Rỗng ⇒ hạ cấp an toàn với chat-drive. */
export async function chatDelegatedDowngradeBlockers(
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<string[]> {
  const blockers: string[] = [];
  for (const { path: workspace } of await readWorkspaceRegistry(registryPath)) {
    const missionsDir = join(workspace, ".avatar", "missions");
    let missionIds: string[];
    try {
      missionIds = await fs.readdir(missionsDir);
    } catch {
      continue;
    }
    for (const missionId of missionIds) {
      try {
        const reason = await missionBlocker(join(missionsDir, missionId, "runtime"));
        if (reason !== undefined) blockers.push(`${workspace}: mission ${missionId} — ${reason}`);
      } catch {
        // mission hỏng/không đọc được: không phải bằng chứng chat-drive.
      }
    }
  }
  return blockers;
}

/** Lý do từ chối `avatar update` (in rồi exit 1), hoặc `undefined` khi được đi tiếp. */
export async function downgradeRefusal(input: {
  current: string;
  target: string;
  allowDowngrade: boolean;
  registryPath?: string;
}): Promise<string | undefined> {
  if (!isDowngrade(input.current, input.target)) return undefined;
  if (!input.allowDowngrade) {
    return (
      `Tag trỏ v${input.target} THẤP hơn bản đang chạy v${input.current} — từ chối hạ cấp. ` +
      "Cố ý hạ: thêm --allow-downgrade (khi không còn mission chat-drive chưa archive)."
    );
  }
  const blockers = await chatDelegatedDowngradeBlockers(input.registryPath);
  if (blockers.length === 0) return undefined;
  return [
    `Không hạ v${input.current} → v${input.target}: bản cũ không hiểu quyết định ủy nhiệm của Claude.`,
    ...blockers.map((b) => `  - ${b}`),
    "Xử lý/archive các mission trên trước (avatar autopilot archive), rồi chạy lại.",
  ].join("\n");
}
